/**
 * Settings Service
 *
 * Manages user preferences and application settings including audio options,
 * notifications, reminders, and other personalization features.
 */

import { SERVICE_NAMES } from "../core/constants";
import { InternalSDKOperations, VoiceSpeed } from "../types";
import { BaseService } from "./base-service";
import { NetworkDetectionManager } from "../utils/network-detection-manager";
import type { WeekDaySelection } from "../types/common";

/**
 * Service class for managing user settings and preferences
 */
export class SettingsService extends BaseService {
  protected serviceName = SERVICE_NAMES.SETTINGS;
  /**
   * Creates a new SettingsService instance
   * @param internalOps - Internal SDK operations interface
   * @param networkManager - Optional shared network detection manager
   */
  constructor(
    internalOps: InternalSDKOperations,
    networkManager?: NetworkDetectionManager
  ) {
    super(internalOps, networkManager);
  }

  async updateIsEffectEnabled(
    isEffectEnabled: boolean,
    version: string = "v1"
  ): Promise<void> {
    const functionName = "updateIsEffectEnabled";
    const parameters = { isEffectEnabled };

    await this.executeWithOfflineSupport(functionName, parameters, () =>
      this.internalOps.makeRequest<{}>(
        SERVICE_NAMES.SETTINGS,
        functionName,
        parameters,
        version
      )
    );
  }

  async updateIsReminderOn(
    isReminderOn: boolean,
    version: string = "v1"
  ): Promise<void> {
    const functionName = "updateIsReminderOn";
    const parameters = { isReminderOn };

    await this.executeWithOfflineSupport(functionName, parameters, () =>
      this.internalOps.makeRequest<{}>(
        SERVICE_NAMES.SETTINGS,
        functionName,
        parameters,
        version
      )
    );
  }

  async updateIsSoundAutoPlayOn(
    isSoundAutoPlayOn: boolean,
    version: string = "v1"
  ): Promise<void> {
    const functionName = "updateIsSoundAutoPlayOn";
    const parameters = { isSoundAutoPlayOn };

    await this.executeWithOfflineSupport(functionName, parameters, () =>
      this.internalOps.makeRequest<{}>(
        SERVICE_NAMES.SETTINGS,
        functionName,
        parameters,
        version
      )
    );
  }

  async updateIsTranscriptEnabled(
    isTranscriptEnabled: boolean,
    version: string = "v1"
  ): Promise<void> {
    const functionName = "updateIsTranscriptEnabled";
    const parameters = { isTranscriptEnabled };

    await this.executeWithOfflineSupport(functionName, parameters, () =>
      this.internalOps.makeRequest<{}>(
        SERVICE_NAMES.SETTINGS,
        functionName,
        parameters,
        version
      )
    );
  }

  async updateIsKeyboardShortcutEnabled(
    isKeyboardShortcutEnabled: boolean,
    version: string = "v1"
  ): Promise<void> {
    const functionName = "updateIsKeyboardShortcutEnabled";
    const parameters = { isKeyboardShortcutEnabled };

    await this.executeWithOfflineSupport(functionName, parameters, () =>
      this.internalOps.makeRequest<{}>(
        SERVICE_NAMES.SETTINGS,
        functionName,
        parameters,
        version
      )
    );
  }

  async updateIsWeeklyProgressOn(
    isWeeklyProgressOn: boolean,
    version: string = "v1"
  ): Promise<void> {
    const functionName = "updateIsWeeklyProgressOn";
    const parameters = { isWeeklyProgressOn };

    await this.executeWithOfflineSupport(functionName, parameters, () =>
      this.internalOps.makeRequest<{}>(
        SERVICE_NAMES.SETTINGS,
        functionName,
        parameters,
        version
      )
    );
  }

  async updateReminders(
    reminders: {
      reminderGoal: number;
      weekDays: WeekDaySelection[];
      reminderTime: {
        hour: number; // 0-23
        minute: number; // 0-59
      };
    },
    version: string = "v1"
  ): Promise<void> {
    const functionName = "updateReminders";
    const parameters = { reminders };

    await this.executeWithOfflineSupport(functionName, parameters, () =>
      this.internalOps.makeRequest<{}>(
        SERVICE_NAMES.SETTINGS,
        functionName,
        parameters,
        version
      )
    );
  }

  async updateVoiceSpeed(
    voiceSpeed: VoiceSpeed,
    version: string = "v1"
  ): Promise<void> {
    const functionName = "updateVoiceSpeed";
    const parameters = { voiceSpeed };

    await this.executeWithOfflineSupport(functionName, parameters, () =>
      this.internalOps.makeRequest<{}>(
        SERVICE_NAMES.SETTINGS,
        functionName,
        parameters,
        version
      )
    );
  }
}
